import React from "react";
import styled from "styled-components";
import { FiArrowUpRight, FiExternalLink, FiGithub, FiMaximize2 } from "react-icons/fi";

const Project = ({ project, featured = false, index }) => {
  const LinkIcon = project.source ? FiGithub : FiExternalLink;
  return (
    <Card $featured={featured} aria-labelledby={`project-${project.id}`}>
      <Preview href={project.image} target="_blank" rel="noreferrer" aria-label={`Open ${project.title} screenshot at full size (new tab)`}>
        <PreviewBar aria-hidden="true"><span><i /><i /><i /></span><span>Project preview</span><FiMaximize2 /></PreviewBar>
        <ImageArea><img src={project.image} alt={project.imageAlt} loading="lazy" decoding="async" /></ImageArea>
        <PreviewCaption aria-hidden="true">Take a closer look <FiArrowUpRight /></PreviewCaption>
      </Preview>
      <Details $featured={featured}>
        <Category><span>{project.category}</span><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span></Category>
        <h3 id={`project-${project.id}`}>{project.title}</h3>
        <Description>{project.description}</Description>
        <Technologies aria-label={`${project.title} technologies`}>
          {project.technologies.map(({ label, Icon }) => <li key={label}><Icon aria-hidden="true" />{label}</li>)}
        </Technologies>
        <ProjectLink href={project.href} target="_blank" rel="noreferrer" aria-label={`${project.linkLabel}: ${project.title} (new tab)`}><LinkIcon aria-hidden="true" /><span>{project.linkLabel}</span><FiArrowUpRight aria-hidden="true" /></ProjectLink>
      </Details>
    </Card>
  );
};

export default Project;

const Card = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  min-width: 0;
  padding: 0.6rem;
  border: 1px solid #d9e6dd;
  border-radius: 1.35rem;
  background: #fcfdfc;
  color: #253d31;
  box-shadow: 0 8px 24px #07170f0d;

  a:focus-visible { outline: 2px solid #008369; outline-offset: 3px; }

  @media (min-width: 700px) {
    grid-column: ${({ $featured }) => $featured ? "1 / -1" : "auto"};
  }
  @media (min-width: 840px) {
    grid-template-columns: ${({ $featured }) => $featured ? "minmax(0, 1.2fr) minmax(0, 1fr)" : "minmax(0, 1fr)"};
    align-items: ${({ $featured }) => $featured ? "center" : "stretch"};
    padding: ${({ $featured }) => $featured ? "1rem" : "0.6rem"};
  }
`;
const Preview = styled.a`
  display: block;
  min-width: 0;
  border: 1px solid #dfe8e2;
  border-radius: 0.85rem;
  background: #edf3ef;
  color: #53695b;
  text-decoration: none;
`;
const PreviewBar = styled.span`
  display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  min-height: 36px; padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #dfe8e2; font-size: 0.6rem;
  > span:first-child { display: inline-flex; gap: 4px; }
  i { width: 5px; height: 5px; border-radius: 50%; background: #adc2b3; }
  > svg { width: 0.8rem; height: 0.8rem; }
`;
const ImageArea = styled.span`
  display: flex; align-items: center; justify-content: center;
  aspect-ratio: 16 / 9; padding: 0.65rem;
  img { display: block; width: 100%; height: 100%; object-fit: contain; border-radius: 0.25rem; }
  @media (min-width: 600px) { padding: 1rem; }
`;
const PreviewCaption = styled.span`
  display: flex; justify-content: flex-end; align-items: center; gap: 0.35rem;
  min-height: 32px; padding: 0.25rem 0.75rem 0.6rem; color: #53695b; font-size: 0.6rem;
`;
const Details = styled.div`
  display: flex; flex-direction: column; min-width: 0; padding: 1.25rem 0.65rem 0.65rem;
  h3 { margin-top: 0.65rem; font-size: 1.5rem; font-weight: 500; letter-spacing: -0.045em; line-height: 1.25; }
  @media (min-width: 600px) { padding: 1.5rem 1.15rem 1rem; }
  @media (min-width: 840px) { h3 { font-size: ${({ $featured }) => $featured ? "2rem" : "1.5rem"}; } }
`;
const Category = styled.p`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem;
  color: #65736b; font-size: 0.6rem; line-height: 1.7; letter-spacing: 0.055em; text-transform: uppercase;
  span:last-child { color: #008369; }
`;
const Description = styled.p`margin-top: 0.85rem; color: #596b60; font-size: 0.8rem; line-height: 1.85;`;
const Technologies = styled.ul`
  display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 1.25rem 0 1.5rem; list-style: none;
  li { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.35rem 0.65rem; border: 1px solid #dfe8e2; border-radius: 999px; color: #476259; font-size: 0.65rem; line-height: 1.6; }
  svg { flex-shrink: 0; font-size: 0.85rem; }
`;
const ProjectLink = styled.a`
  display: flex; align-items: center; gap: 0.6rem; min-height: 48px;
  margin-top: auto; padding-top: 0.75rem; border-top: 1px solid #dfe8e2;
  color: #007b61; font-size: 0.75rem; font-weight: 600; text-decoration: none;
  span { flex: 1; }
  > svg { flex-shrink: 0; font-size: 1rem; }
  &:hover span { text-decoration: underline; text-underline-offset: 4px; }
`;
